import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { 
  Bell, Heart, MessageCircle, UserPlus, Briefcase, Trophy,
  CheckCheck, Loader2, Filter, Clock, Mail
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { notificationsAPI } from '../services/api';
import { toast } from 'sonner';

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [markingAllRead, setMarkingAllRead] = useState(false);

  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 8 seconds
    const pollInterval = setInterval(() => {
      fetchNotificationsQuiet();
    }, 8000);
    
    return () => clearInterval(pollInterval);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getNotifications(100);
      const notificationsList = response.data.notifications || response.data || [];
      setNotifications(notificationsList);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationsQuiet = async () => {
    try {
      const response = await notificationsAPI.getNotifications(100);
      const notificationsList = response.data.notifications || response.data || [];
      setNotifications(notificationsList);
    } catch (error) {
      // Silent fail on polling
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setMarkingAllRead(true);
      await notificationsAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      await handleMarkAsRead(notif._id);
    }

    // Navigate based on notification type
    if (notif.type === 'follow' || notif.type === 'follow_accepted') {
      navigate(`/profile/${notif.user?.username}`);
    } else if (notif.type === 'follow_request') {
      navigate('/connections?tab=requests');
    } else if (notif.type === 'like' || notif.type === 'comment') {
      navigate(`/home`); // Could navigate to specific post if we have postId
    } else if (notif.type === 'message') {
      navigate('/messages');
    } else if (notif.type === 'job_application') {
      navigate('/dashboard');
    } else if (notif.type === 'trial_application') {
      navigate('/dashboard');
    } else if (notif.type === 'connection') {
      navigate(`/profile/${notif.user?.username}`);
    } else if (notif.type === 'enquiry') {
      navigate('/dashboard');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'like':
        return <Heart className="w-5 h-5 text-primary" />;
      case 'comment':
        return <MessageCircle className="w-5 h-5 text-purple-400" />;
      case 'follow':
      case 'follow_accepted':
      case 'connection':
        return <UserPlus className="w-5 h-5 text-purple-500" />;
      case 'follow_request':
        return <UserPlus className="w-5 h-5 text-purple-300" />;
      case 'job_application':
        return <Briefcase className="w-5 h-5 text-purple-600" />;
      case 'trial_application':
        return <Trophy className="w-5 h-5 text-purple-400" />;
      case 'enquiry':
        return <Mail className="w-5 h-5 text-purple-500" />;
      case 'message':
        return <MessageCircle className="w-5 h-5 text-primary" />;
      default:
        return <Bell className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatDateHeader = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === now.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else if ((now - date) / (1000 * 60 * 60 * 24) < 7) {
      return date.toLocaleDateString('en-GB', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('en-GB', { 
        day: 'numeric', 
        month: 'long',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  // Group notifications by date
  const groupNotificationsByDate = (notifications) => {
    const groups = {};
    notifications.forEach(notif => {
      const date = new Date(notif.createdAt).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(notif);
    });
    return groups;
  };

  // Filter notifications
  const filteredNotifications = notifications.filter(notif => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notif.read;
    return notif.type === filter;
  });

  const groupedNotifications = groupNotificationsByDate(filteredNotifications);
  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="container mx-auto px-4 pt-20 pb-24 md:pb-6 max-w-2xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Bell className="w-7 h-7 text-primary" />
            <h1 className="text-2xl font-bold">Notifications</h1>
            {unreadCount > 0 && (
              <Badge className="bg-primary text-white">
                {unreadCount} new
              </Badge>
            )}
          </div>
          
          {unreadCount > 0 && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={markingAllRead}
            >
              {markingAllRead ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCheck className="w-4 h-4 mr-2" />
              )}
              Mark all read
            </Button>
          )}
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Notifications</SelectItem>
                  <SelectItem value="unread">Unread Only</SelectItem>
                  <SelectItem value="like">Likes</SelectItem>
                  <SelectItem value="comment">Comments</SelectItem>
                  <SelectItem value="follow">Follows</SelectItem>
                  <SelectItem value="connection">Connections</SelectItem>
                  <SelectItem value="message">Messages</SelectItem>
                  <SelectItem value="job_application">Job Applications</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchNotifications}
                className="ml-auto"
              >
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Notifications List */}
        {loading ? (
          <div className="space-y-3" data-testid="notifications-skeleton">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <Card key={i}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="relative">
                      <Skeleton className="w-12 h-12 rounded-full" />
                      <Skeleton className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-3.5 w-3/4" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                    <Skeleton className="w-3 h-3 rounded-full flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Bell className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No notifications</h3>
              <p className="text-muted-foreground">
                {filter === 'unread' 
                  ? "You're all caught up! No unread notifications."
                  : "When you get notifications, they'll appear here."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedNotifications).map(([date, notifs]) => (
              <div key={date}>
                {/* Date Header */}
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">
                    {formatDateHeader(notifs[0].createdAt)}
                  </span>
                  <div className="flex-1 h-px bg-border"></div>
                </div>

                {/* Notifications for this date */}
                <div className="space-y-2">
                  {notifs.map((notif) => (
                    <Card 
                      key={notif._id}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        !notif.read ? 'bg-primary/5 border-primary/20' : ''
                      }`}
                      onClick={() => handleNotificationClick(notif)}
                    >
                      <CardContent className="py-4">
                        <div className="flex items-start gap-4">
                          {/* User Avatar */}
                          <div className="relative">
                            <Avatar className="w-12 h-12">
                              <AvatarImage 
                                src={notif.user?.profileImage} 
                                alt={notif.user?.name} 
                              />
                              <AvatarFallback>
                                {notif.user?.name?.[0] || 'U'}
                              </AvatarFallback>
                            </Avatar>
                            {/* Notification Type Icon */}
                            <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-1 border">
                              {getNotificationIcon(notif.type)}
                            </div>
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm">
                              <span className="font-semibold">
                                {notif.user?.name || 'Someone'}
                              </span>{' '}
                              <span className="text-muted-foreground">
                                {notif.text}
                              </span>
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {getTimeAgo(notif.createdAt)}
                            </p>
                          </div>

                          {/* Unread Indicator */}
                          {!notif.read && (
                            <div className="w-3 h-3 rounded-full bg-primary flex-shrink-0"></div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Load More - if needed */}
        {filteredNotifications.length >= 100 && (
          <div className="text-center mt-6">
            <p className="text-sm text-muted-foreground">
              Showing last 100 notifications
            </p>
          </div>
        )}
      </main>

    </div>
  );
};

export default Notifications;
